'use client';

import { useState, useCallback, useEffect, useRef, useLayoutEffect, type FC, type CSSProperties } from 'react';
import { cn } from '../../utils/cn';
import { getGhostSrc } from '../../utils/ghostSkeleton';
import { forgetImageLoaded, isImageLoaded, markImageLoaded } from './loadedImages';

/**
 * Сколько ждать картинку, которую набор считает загруженной, прежде чем всё-таки
 * показать силуэт. Два кадра — ровно чтобы покрыть случай «браузер отдаёт картинку из
 * кэша не в этом тике, а в следующем», и не больше: если она не пришла и за это время,
 * значит её из кэша выкинули и идёт настоящая загрузка, а на ней пустая коробка хуже
 * силуэта. Замерено: с отсрочкой в 120 мс пустота держалась заметные семь кадров.
 */
const CACHE_GRACE_MS = 32;

interface SkinImageProps {
  /** Market hash name — задаёт ghost-силуэт модели. */
  name: string;
  src?: string | null;
  alt?: string;
  /** Классы на обе картинки (размер/позиция/тень). */
  imgClassName?: string;
  /** Inline-стиль только на реальную картинку (фильтры/трансформы/анимация раскрытия). */
  imgStyle?: CSSProperties;
  className?: string;
  style?: CSSProperties;
  /** Грузить сразу, не дожидаясь попадания во вьюпорт — для картинки над сгибом
   *  (открытие кейса, экран айтема), где ленивая загрузка добавила бы задержку. */
  priority?: boolean;
}

/**
 * Картинка скина с ghost-заглушкой. Пока грузится (или если не загрузилась)
 * реальная картинка — показываем серый силуэт модели из public/ghosts.
 *
 * Адрес, который уже грузился в этой вкладке, стартует сразу готовым: силуэта и
 * проявления на возврате нет вовсе. Силуэт остаётся для честной загрузки и для отказа.
 */
export const SkinImage: FC<SkinImageProps> = ({
  name, src, alt = name, imgClassName, imgStyle, className, style, priority = false,
}) => {
  const ghost = getGhostSrc(name);
  // Первый рендер уже знает, что адрес загружался: `useState` с функцией читает
  // множество один раз, на монтировании, и первый кадр рисуется готовым.
  const [isLoaded, setIsLoaded] = useState(() => isImageLoaded(src));
  const [hasFailed, setHasFailed] = useState(false);
  // Картинка была готова ещё до монтирования — проявлять нечего, переход не нужен.
  const [instant, setInstant] = useState(() => isImageLoaded(src));
  const prevSrcRef = useRef(src);
  const nodeRef = useRef<HTMLImageElement | null>(null);

  useLayoutEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      const known = isImageLoaded(src);
      setIsLoaded(known);
      setInstant(known);
      setHasFailed(false);
    }
  }, [src]);

  // Уже лежащая в кэше картинка приходит в ref готовой (`complete`) и `onLoad` по ней не
  // сработает — снимаем состояние с самого элемента. Отдельная проба через `new Image()`
  // здесь стояла раньше и стоила лишнего сетевого запроса на каждый скин, мимо ленивой загрузки.
  const captureNode = useCallback(
    (node: HTMLImageElement | null) => {
      nodeRef.current = node;
      if (!node) return;
      if (node.complete && node.naturalWidth > 0) {
        markImageLoaded(src);
        setIsLoaded(true);
      }
      // Обратного хода тут намеренно нет. `complete === false` на этом кадре — обычное
      // дело даже для кэшированной картинки: браузер отдаёт её на следующем тике. Если
      // на этом основании возвращать силуэт, вернётся ровно то мигание, ради которого
      // всё и затевалось. Адрес выбывает из множества только по настоящему отказу.
    },
    [src],
  );

  // Ставка на набор оказалась неверной: адрес числился загруженным, а картинка не
  // приехала за отведённое время. Возвращаем силуэт и убираем адрес из набора, чтобы
  // следующее монтирование не наступило на те же грабли.
  useEffect(() => {
    if (!src || !instant || !isLoaded) return;
    const node = nodeRef.current;
    if (node?.complete && node.naturalWidth > 0) return;

    const timer = window.setTimeout(() => {
      const current = nodeRef.current;
      if (current && current.complete && current.naturalWidth > 0) return;
      forgetImageLoaded(src);
      setIsLoaded(false);
      setInstant(false);
    }, CACHE_GRACE_MS);

    return () => window.clearTimeout(timer);
  }, [src, instant, isLoaded]);

  const handleLoad = useCallback(() => {
    markImageLoaded(src);
    setIsLoaded(true);
  }, [src]);

  const handleError = useCallback(() => {
    forgetImageLoaded(src);
    setHasFailed(true);
  }, [src]);

  const showGhost = !src || !isLoaded || hasFailed;

  return (
    <div className={cn('relative w-full h-full', className)} style={style}>
      {showGhost && (
        <img
          src={ghost}
          alt=""
          aria-hidden
          decoding="async"
          className={cn('absolute inset-0 opacity-50 pointer-events-none', imgClassName)}
        />
      )}
      {src && !hasFailed && (
        <img
          ref={captureNode}
          src={src}
          alt={alt}
          // Ленивость и асинхронное декодирование стоят кадра. Картинке, которая уже
          // грузилась, откладывать нечего — она отдаётся из кэша и декодируется сразу.
          loading={priority || instant ? 'eager' : 'lazy'}
          decoding={instant ? 'sync' : 'async'}
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={handleLoad}
          onError={handleError}
          style={imgStyle}
          className={cn(
            'relative',
            // Переход нужен только когда картинка правда догружалась при этом
            // монтировании. Кэшированную он заставлял честно проявляться 200 мс.
            instant ? undefined : 'transition-opacity duration-200',
            isLoaded ? 'opacity-100' : 'opacity-0',
            imgClassName,
          )}
        />
      )}
    </div>
  );
};
